import * as actionTypes from './_actionTypes';
import {
	updateObject,
	getFormValidity,
	getOverallValidity,
	getFormIdByAppId,
	objectInsertAtIndex,
	checkValidityRules,
	deepCloneObject,
	getComponentByComponentId,
	generateMemberKey,
} from '../utility';
import { RequestTypes } from '../../constants/enums';

const initialState = {
	loading: false,
	submitting: false,
	submissionAttempted: false,
	allFormValidity: true,
	allFormsSubmitted: false,
	allForms: [],
	allFormsInitial: [],
	appDependencies: {},
	trackingNumber: '',
	activeIndex: 0,
	appsData: [],
	tabApps: [],
	requestType: RequestTypes.SINGLE,
	esos: [],
	rules: null,
	lastUpdated: Date.now(),
	numContainers: 0,
	newSponsorESOID: null,
};

const addApp = (state, action) => {
	const incomingAction = { ...action.app.appData.ContractModel };
	const incomingActionMembers = { ...incomingAction.members };
	let processedMembers = {};

	for (let i in incomingActionMembers) {
		processedMembers[i] = incomingActionMembers[i];
	}

	incomingAction.members = processedMembers;

	const newApp = {
		id: action.app.appData.id,
		name: action.app.appData.Name,
		valid: action.app.appData.Submitted,
		submitted: action.app.appData.Submitted,
		componentType: action.app.appData.ComponentType,
		roleDescriptionLink: action.app.appData.RoleLink,
		model: action.app.appData.ContractModel,
		extra: null,
	};

	let appExists = false;
	for (let app in state.allForms) {
		if (state.allForms[app].id === newApp.id) {
			appExists = true;
		}
	}

	if (appExists) {
		return state;
	} else {
		return updateObject(state, {
			loading: false,
			allForms: state.allForms.concat(newApp),
			allFormsInitial: state.allFormsInitial.concat(
				deepCloneObject(newApp)
			),
			allFormValidity:
				state.allFormValidity && action.app.appData.Submitted,
			trackingNumber: action.app.trackingNumber,
		});
	}
};

const getComponentByKey = (members, targetKey) => {
	const index = Object.keys(members).findIndex(m => m === targetKey);
	if (index > -1) {
		return [targetKey, Object.values(members)[index]];
	}
	return [targetKey, null];
};

const getComponentChildrenById = (members, parentMap, targetId) => {
	const childrenIds = parentMap[targetId] ? parentMap[targetId] : [];
	/* eslint no-sequences: "off" */
	const childrenMembers = Object.keys(members)
		.filter(key => childrenIds.includes(members[key].id))
		.reduce((res, key) => ((res[key] = members[key]), res), {});
	return childrenMembers;
};

const getComponentById = (members, targetId) => {
	const index = Object.values(members).findIndex(m => m.id === targetId);
	if (index > -1) {
		return [Object.keys(members)[index], Object.values(members)[index]];
	}
	return [null, null];
};

const generateContainerBase = () => {
	//floor(rand() * (max - min) + min) - generates new random number between max (exclusive) and min (inclusive)
	return Math.floor(Math.random() * (900000000 - 100000000) + 100000000);
};

const generateContainerId = (oldId, base) => {
	return base + oldId;
};

/**
 * Add new Container set to the component members based on a template
 * @param {Object} state existing state
 * @param {*} action action dispatched (contains `container: { appId, componentKey}`)
 * @returns updated state
 */
const addNewContainer = (state, action) => {
	//get payload
	const { appId, componentKey } = { ...action.container };

	// get the correct app request form
	const formId = getFormIdByAppId(state, appId);

	//copy state pieces
	let newState = { ...state };
	let newMembers = { ...newState.allForms[formId].model.members };
	const newComponentMap = { ...newState.allForms[formId].model.componentMap };
	const newChildrenComponentMap = {
		...newState.allForms[formId].model.childrenComponentMap,
	};

	// establish insertion time
	const dateStamp = Date.now();
	//establish ID base
	const newIdBase = generateContainerBase();

	//get the container list component
	const [containerListKey, containerListComponent] = getComponentByKey(
		newMembers,
		componentKey
	);

	if (containerListComponent) {
		// determine where to start inserting the cloned members
		let insertIndex =
			Object.keys(newMembers).findIndex(key => key === containerListKey) +
			1;

		// get all children members
		let childrenMemberValues = [];
		Object.values(
			getComponentChildrenById(
				newMembers,
				newComponentMap,
				containerListComponent.id
			)
		).forEach(value => {
			childrenMemberValues.push({
				parentId: containerListComponent.id,
				childId: value,
			});
		});

		//for each child
		while (childrenMemberValues.length > 0) {
			//set the value of the new "child"
			const childMemberId = childrenMemberValues[0].childId.id;
			const [, childMember] = getComponentById(newMembers, childMemberId);

			//set the parent ID
			const parentId = childrenMemberValues[0].parentId;

			//determine if member should be cloned
			let newExtras = {};
			if (childMember.extras) {
				newExtras = JSON.parse(childMember.extras);
			}
			//insert the cloned components
			if (!('containerList' in newExtras)) {
				//clone
				const clonedMember = deepCloneObject(childMember);

				// update the IDs
				const newId = generateContainerId(childMember.id, newIdBase);
				clonedMember.originalId = childMember.id;
				clonedMember.id = newId;
				clonedMember.visible = true;
				clonedMember.elementConfig.options[newId] = deepCloneObject(
					clonedMember.elementConfig.options[childMember.id]
				);
				delete clonedMember.elementConfig.options[childMember.id];

				//update additional metadata
				clonedMember.validation.valid =
					!clonedMember.elementConfig.options[newId].Required;
				clonedMember.validation.trueValidity = false;
				newExtras['containerList'] = true;
				clonedMember.extras = JSON.stringify(newExtras);
				if (clonedMember.populationRoute) {
					try {
						const populationRoute = JSON.parse(
							clonedMember.populationRoute
						);
						if (populationRoute) {
							const actionTypes = [
								'disableActions',
								'showActions',
								'populateActions',
								'setActions',
							];
							for (const actionType of actionTypes) {
								if (populationRoute[actionType]) {
									const clonedActions = deepCloneObject(
										populationRoute[actionType]
									);
									for (const action of populationRoute[
										actionType
									]) {
										if (action.targetComponentKey) {
											const [, originalMember] =
												getComponentByKey(
													newMembers,
													action.targetComponentKey
												);
											action.targetComponentKey = `${generateContainerId(
												originalMember.id,
												newIdBase
											)}_${originalMember.label.toLowerCase()}`;
										}
										if (action.watchComponentKey) {
											const [, originalMember] =
												getComponentByKey(
													newMembers,
													action.watchComponentKey
												);
											action.watchComponentKey = `${generateContainerId(
												originalMember.id,
												newIdBase
											)}_${originalMember.label.toLowerCase()}`;
										}
										clonedActions.push(action);
									}
								}
							}
							clonedMember.populationRoute =
								JSON.stringify(populationRoute);
						}
					} catch (e) {}
				}

				//perform the insert
				newMembers = objectInsertAtIndex(
					newMembers,
					generateMemberKey(clonedMember),
					clonedMember,
					insertIndex
				);

				//increment the index
				insertIndex++;

				//update the parent map:  clone > predicted children
				if (!(newId in newComponentMap)) {
					newComponentMap[newId] = [];
				}
				for (const oldId of newComponentMap[childMember.id]) {
					const [, tempMember] = getComponentById(newMembers, oldId);
					const newEntryId = generateContainerId(
						tempMember.id,
						newIdBase
					);
					if (!newComponentMap[newId].includes(newEntryId)) {
						newComponentMap[newId].push(newEntryId);
					}
				}
				//update the parent map:  parent > clone
				if (!(parentId in newComponentMap)) {
					newComponentMap[parentId] = [];
				}
				if (!newComponentMap[parentId].includes(newId)) {
					newComponentMap[parentId].push(newId);
				}
				//update child map:  clone < parent
				if (!(newId in newChildrenComponentMap)) {
					newChildrenComponentMap[newId] = [parentId];
				}
				if (!newChildrenComponentMap[newId].includes(parentId)) {
					newChildrenComponentMap.push(parentId);
				}

				//populate new children (if applicable)
				Object.values(
					getComponentChildrenById(
						newMembers,
						newComponentMap,
						childMember.id
					)
				).forEach(value => {
					childrenMemberValues.push({
						parentId: clonedMember.id,
						childId: value,
					});
				});
			}

			//dequeue the first value from the list to avoid infinite loop
			childrenMemberValues.splice(0, 1);
		}
	}

	const newContainerNum = newState.allForms[formId].numContainers
		? newState.allForms[formId].numContainers + 1
		: 1;

	const newForm = updateObject(newState.allForms[formId], {
		numContainers: newContainerNum,
		model: updateObject(newState.allForms[formId].model, {
			members: newMembers,
			componentMap: newComponentMap,
			childrenComponentMap: newChildrenComponentMap,
		}),
	});

	newState.allForms[formId] = newForm;
	newState = updateObject(newState, {
		lastUpdated: dateStamp,
	});

	newState.allForms[formId].model.members[componentKey].value =
		newContainerNum;

	const validityResult = checkValidityRules(
		newState.allForms[formId].model.members[componentKey]
	);

	let validationResult = {
		data: {
			formId: formId,
			memberId: componentKey,
			memberValid: null,
			touched: null,
			validationMessage: null,
			value: null,
			visible: null,
		},
	};
	validationResult.data = {
		...validationResult.data,
		memberValid: validityResult.valid,
		touched:
			newState.allForms[formId].model.members[componentKey].validation
				.touched,
		validationMessage: validityResult.message,
		value: newContainerNum,
		visible: newState.allForms[formId].model.members[componentKey].visible,
	};
	newState = checkValidity(newState, {
		validationResult: validationResult,
	});

	return newState;
};

const appSubmitted = (state, action) => {
	//update this.state.allForms[i].submitted to true
	const id = action.submissionDetails.formId;

	const updatedSubmit = updateObject(state.allForms[id], { submitted: true });

	const updatedAllForms = { ...state.allForms };
	updatedAllForms[id] = updatedSubmit;
	//loop through all updatedAllForms[i].submitted to update state.allFormsSubmitted
	let isAllSubmitted = true;
	for (let i in updatedAllForms) {
		isAllSubmitted = isAllSubmitted && updatedAllForms[i].submitted;
	}

	const updatedState = updateObject(state, {
		allFormsSubmitted: isAllSubmitted,
		allForms: updatedAllForms,
	});

	return updatedState;
};

const callApiPopulateSuccess = (state, response) => {
	const action = response.apiRouteInfo.action;
	const formIndex = response.apiRouteInfo.formIndex;
	let updatedState = { ...state };
	let updatedAllForms = { ...updatedState.allForms };
	updatedState.loading = true;

	if (action.disableActions !== null) {
		for (let i in action.disableActions) {
			const disableAction = action.disableActions[i];
			let updatedMember =
				updatedAllForms[formIndex].model.members[
					disableAction.targetComponentKey
				];
			let validationResult = {
				data: {
					formId: formIndex,
					memberId: disableAction.targetComponentKey,
					memberValid: null,
					validationMessage: null,
					value: null,
					visible: null,
				},
			};
			let extras = JSON.parse(updatedMember.extras);
			switch (disableAction.disableType) {
				case 'component':
					if (extras === null) {
						extras = { disabled: false };
					}
					if (!extras.disabled) {
						extras['disabled'] = [];
					}
					extras['disabled'] = disableAction.disabled;
					break;
				case 'data':
					if (extras === null) {
						extras = { disabled: [] };
					}
					if (!extras.disabled) {
						extras['disabled'] = [];
					}
					if (disableAction.disabled) {
						if (!extras.disabled.includes(disableAction.ID)) {
							extras.disabled = [
								...extras.disabled,
								disableAction.ID,
							];
						}
					} else {
						if (extras.disabled.includes(disableAction.ID)) {
							extras.disabled.splice(
								extras.disabled.indexOf(disableAction.ID),
								1
							);
						}
					}
					break;
				default:
					break;
			}
			updatedMember.extras = JSON.stringify(extras);

			// check validation
			const validityResult = checkValidityRules(updatedMember);
			validationResult.data = {
				...validationResult.data,
				memberValid: validityResult.valid,
				touched: updatedMember.validation.touched,
				validationMessage: validityResult.message,
				value: updatedMember.value,
				visible: updatedMember.visible,
			};
			updatedState = checkValidity(updatedState, {
				validationResult: validationResult,
			});
		}
	}

	if (action.showActions !== null) {
		for (let i in action.showActions) {
			const showAction = action.showActions[i];
			let updatedMember =
				updatedAllForms[formIndex].model.members[
					showAction.targetComponentKey
				];
			let validationResult = {
				data: {
					formId: formIndex,
					memberId: showAction.targetComponentKey,
					memberValid: null,
					validationMessage: null,
					value: null,
					visible: null,
				},
			};
			const updatedElementConfig = { ...updatedMember.elementConfig };
			const optionKey = Object.keys(
				state.allForms[formIndex].model.members[
					showAction.targetComponentKey
				].elementConfig.options
			)[0];
			updatedElementConfig.options[optionKey].Visible =
				showAction.visible;
			updatedMember.visible = action.forceChildrenHidden
				? false
				: showAction.visible;
			updatedMember.validation.valid = showAction.visible
				? updatedMember.validation.trueValidity
				: true;
			updatedMember.elementConfig = updatedElementConfig;
			updatedAllForms[formIndex].model.members[
				showAction.targetComponentKey
			] = updatedMember;
			// check validation
			const validityResult = checkValidityRules(updatedMember);
			validationResult.data = {
				...validationResult.data,
				memberValid: validityResult.valid,
				touched: updatedMember.validation.touched,
				validationMessage: validityResult.message,
				value: updatedMember.value,
				visible: updatedMember.visible,
			};
			updatedState = checkValidity(updatedState, {
				validationResult: validationResult,
			});
		}
	}

	if (action.populateActions !== null) {
		for (let i in action.populateActions) {
			const populateAction = action.populateActions[i];
			let updatedMember =
				updatedAllForms[formIndex].model.members[
					populateAction.targetComponentKey
				];
			let extras = JSON.parse(updatedMember.extras);
			const updatedElementConfig = { ...updatedMember.elementConfig };
			const optionKey = Object.keys(
				state.allForms[formIndex].model.members[
					populateAction.targetComponentKey
				].elementConfig.options
			)[0];
			updatedElementConfig.options[optionKey].ApplicationData =
				populateAction.appData;

			let val = null;
			let valFound = false;
			//check to see if currently selected value is still available in the new key-value pairs from apiData

			if (updatedMember.elementType !== 'multiselect') {
				for (let i in populateAction.appData) {
					if (
						populateAction.appData[i].Value === updatedMember.value
					) {
						valFound = true;
						break;
					}
				}
				if (valFound) {
					val = updatedMember.value;
				}
			} else {
				let arrValuesCurrent =
					updatedMember.value && updatedMember.value.length > 0
						? updatedMember.value.split('~~')
						: [];
				let arrValuesUpdated = [];
				// if multiselect searchable, value should never reset to null
				if (
					extras &&
					extras.multiselect &&
					(extras.multiselect.isSearch ||
						!extras.multiselect.isPerishable)
				) {
					val = updatedMember.value;
				} else {
					// perform unsorted comparison of updated value array with current value array
					populateAction.appData.forEach(data => {
						if (arrValuesCurrent.includes(data.Value)) {
							valFound = true;
							arrValuesUpdated.push(data.Value);
						}
					});
					if (valFound) {
						val = arrValuesUpdated.join('~~');
					}
				}
			}

			updatedMember.value = val;
			updatedAllForms[formIndex].model.members[
				populateAction.targetComponentKey
			] = updatedMember;
			updatedState.allForms = updatedAllForms;

			// check validation
			let validationResult = {
				data: {
					formId: formIndex,
					memberId: populateAction.targetComponentKey,
					memberValid: null,
					validationMessage: null,
					value: null,
					visible: null,
				},
			};

			const validityResult = checkValidityRules(updatedMember);

			validationResult.data = {
				...validationResult.data,
				memberValid: validityResult.valid,
				touched: updatedMember.validation.touched,
				validationMessage: validityResult.message,
				value: updatedMember.value,
				visible: updatedMember.visible,
			};

			updatedState = checkValidity(updatedState, {
				validationResult: validationResult,
			});
		}
	}
	if (action.setActions !== null) {
		for (let i in action.setActions) {
			const setAction = action.setActions[i];
			let updatedMember =
				updatedAllForms[formIndex].model.members[
					setAction.targetComponentKey
				];
			let validationResult = {
				data: {
					formId: formIndex,
					memberId: setAction.targetComponentKey,
					memberValid: null,
					validationMessage: null,
					value: null,
					visible: null,
				},
			};
			const updatedElementConfig = { ...updatedMember.elementConfig };
			const optionKey = Object.keys(
				state.allForms[formIndex].model.members[
					setAction.targetComponentKey
				].elementConfig.options
			)[0];
			if (
				updatedElementConfig.options[optionKey].ApplicationData !== null
			) {
				let arrValues =
					updatedMember.value !== null
						? updatedMember.value
								.replace('--select--', '')
								.split('~~')
						: [];
				switch (setAction.type) {
					case 'add':
						if (
							updatedMember.elementType === 'dropdown' ||
							updatedMember.elementType.includes('paragraph')
						) {
							// component with application data and no support for multiple selections
							updatedMember.value =
								setAction.value !== null
									? setAction.value
									: updatedMember.value;
						} else {
							updatedMember.value =
								setAction.value !== null &&
								arrValues.indexOf(setAction.value) === -1
									? updatedMember.value !== ''
										? updatedMember.value
											? updatedMember.value +
											  '~~' +
											  setAction.value
											: setAction.value
										: setAction.value
									: updatedMember.value;
						}
						break;
					case 'remove':
						if (arrValues.indexOf(setAction.value) > -1) {
							// if value to remove present
							arrValues.splice(
								arrValues.indexOf(setAction.value),
								1
							);
						}
						updatedMember.value = arrValues.join('~~');
						break;
					default:
						break;
				}
			} else {
				updatedMember.value =
					setAction.type !== 'remove'
						? setAction.value
						: updatedMember.value;
			}

			// check validation
			const validityResult = checkValidityRules(updatedMember);
			validationResult.data = {
				...validationResult.data,
				memberValid: validityResult.valid,
				touched: updatedMember.validation.touched,
				validationMessage: validityResult.message,
				value: updatedMember.value,
				visible: updatedMember.visible,
			};
			updatedState = checkValidity(updatedState, {
				validationResult: validationResult,
			});
		}
	}

	return updateObject(updatedState, { loading: false });
};

const callApiPopulateStart = (state, action) => {
	return updateObject(state, { loading: true });
};
// const callApiPopulateSuccess = (state, action) => {
//     return updateObject(state, { loading: true });
// };
const callApiPopulateFail = (state, action) => {
	return updateObject(state, { loading: false });
};

const callApiValidate = (state, action) => {
	const apiData = action.apiRouteInfo.data;
	const formIndex = action.apiRouteInfo.formIndex;
	const memberIndex =
		apiData && apiData.memberToValidateKey
			? apiData.memberToValidateKey
			: action.apiRouteInfo.memberIndex;
	let validationValid = true;
	if (apiData !== null && apiData.Key !== null)
		validationValid = apiData.Key.toLowerCase() === 'true';

	let validationMessage = validationValid ? '' : apiData.Value.MemberNames[0];

	let updatedState = { ...state };
	let updatedAllForms = { ...updatedState.allForms };
	let updatedAllFormsForm = { ...updatedAllForms[formIndex] };
	let updatedModel = { ...updatedAllFormsForm.model };
	let updatedMembers = { ...updatedModel.members };
	let updatedMembersMember = { ...updatedMembers[memberIndex] };
	let updatedValidation = { ...updatedMembersMember.validation };

	updatedValidation = updateObject(updatedValidation, {
		message: validationMessage,
		trueValidity: validationValid,
		valid: validationValid,
	});
	updatedMembersMember = updateObject(updatedMembersMember, {
		validation: updatedValidation,
	});
	updatedMembers[memberIndex] = updatedMembersMember;
	updatedModel = updateObject(updatedModel, {
		members: updatedMembers,
	});

	//re-evaluate the current app's validity
	let appValidity = true;
	Object.keys(updatedMembers).every(member => {
		if (!updatedMembers[member].validation.valid) {
			appValidity = false;
		}
		return appValidity;
	});

	updatedAllFormsForm = updateObject(updatedAllFormsForm, {
		model: updatedModel,
		valid: appValidity,
	});
	updatedAllForms[formIndex] = updatedAllFormsForm;

	//re-evaluate the current request's validity
	let allValidity = true;
	Object.keys(updatedAllForms).every(form => {
		if (!updatedAllForms[form].valid) {
			allValidity = false;
		}
		return allValidity;
	});

	updatedState = updateObject(updatedState, {
		allForms: updatedAllForms,
		allFormValidity: allValidity,
	});
	return updatedState;
};

const checkValidity = (state, action) => {
	const appIndex = action.validationResult.data.formId;
	const memberId = action.validationResult.data.memberId;
	const memberValid = action.validationResult.data.visible
		? action.validationResult.data.memberValid
		: true;
	const memberTrueValidity = action.validationResult.data.memberValid;
	const appValue = action.validationResult.data.value;
	const validationMessage = action.validationResult.data.validationMessage;

	//update the validity of the app
	const updatedStateAllForms = { ...state.allForms };

	//update the value of the member
	const updatedStateAllFormsFormModelMembers = {
		...updatedStateAllForms[appIndex].model.members,
	};

	if (updatedStateAllFormsFormModelMembers[memberId].submitted) {
		return state;
	}

	if (memberId && updatedStateAllFormsFormModelMembers[memberId]) {
		updatedStateAllFormsFormModelMembers[memberId].value = appValue;

		//update touched
		updatedStateAllFormsFormModelMembers[memberId].validation.touched =
			action.validationResult.data.hasOwnProperty('touched')
				? action.validationResult.data.touched
				: true;
		updatedStateAllFormsFormModelMembers[memberId].validation.valid =
			memberValid;
		updatedStateAllFormsFormModelMembers[memberId].validation.trueValidity =
			memberTrueValidity;
		updatedStateAllFormsFormModelMembers[memberId].validation.message =
			validationMessage;
	}

	//update the validity of the app
	let appValid = true;
	for (let i in updatedStateAllFormsFormModelMembers) {
		if (updatedStateAllFormsFormModelMembers[i].visible) {
			appValid =
				appValid &&
				updatedStateAllFormsFormModelMembers[i].validation.valid;
		}
	}
	updatedStateAllForms[appIndex].valid = appValid;

	//put it all back together again
	updatedStateAllForms[appIndex].model.members =
		updatedStateAllFormsFormModelMembers;

	//check the validity of the whole request
	let valid = true;
	for (let form in updatedStateAllForms) {
		valid = valid && updatedStateAllForms[form].valid;
	}
	return updateObject(state, {
		allFormValidity: valid,
		allForms: updatedStateAllForms,
	});
};

const checkAllValidity = (state, action) => {
	const members =
		state.allForms[action.payload.formToValidateKey].model.members;
	Object.keys(members).forEach(member => {
		if (!members[member].validation.valid) {
			return;
		}
	});

	return state;
};

const runRules = (state, action) => {
	let updatedFlag = false;
	let updatedState;

	action.action.data.forEach(element => {
		let rulesType = element.actionType;

		switch (rulesType) {
			case 'UPDATE_VALUE':
				let targetComponentKey = element.targetComponentKey;
				let targetAppId = element.targetAppId;
				let value = element.targetComponentValue;
				updatedFlag = true;
				updatedState = updateValue(state, {
					valueUpdate: {
						appId: targetAppId,
						componentKey: targetComponentKey,
						newValue: value,
					},
				});
				break;
			default:
				break;
		}
	});

	if (updatedFlag) {
		return updatedState;
	} else {
		return state;
	}
};

/**
 * Remove components that were dynamically added via the container component
 * @param {*} state current state
 * @param {*} action contains container component to be removed
 * @returns updated state
 */
const removeContainer = (state, action) => {
	// get payload
	const { appId, componentKey } = { ...action.container };

	// get the correct app request form
	const formId = getFormIdByAppId(state, appId);

	// copy state pieces
	let newState = { ...state };
	let newMembers = { ...newState.allForms[formId].model.members };
	const newComponentMap = { ...newState.allForms[formId].model.componentMap };
	const newChildrenComponentMap = {
		...newState.allForms[formId].model.childrenComponentMap,
	};

	// establish removal time
	const dateStamp = Date.now();

	// get the container component
	const [, containerComponent] = getComponentByKey(newMembers, componentKey);

	//get the container list component
	const containerListId = newChildrenComponentMap[containerComponent.id][0];
	const [containerListKey, containerListComponent] = getComponentById(
		newMembers,
		containerListId
	);

	if (containerComponent) {
		//for each component to remove
		let removalIds = [containerComponent.id];
		while (removalIds.length > 0) {
			const removalId = removalIds[0];
			const [removalKey, removalComponent] = getComponentById(
				newMembers,
				removalId
			);

			if (removalComponent) {
				// queue up more children to remove before deleting the records
				removalIds.push(...newComponentMap[removalComponent.id]);

				// remove the component member
				delete newMembers[removalKey];

				// clean up the parent map
				const parentId =
					newChildrenComponentMap[removalComponent.id][0];
				if (parentId && newComponentMap[parentId]) {
					newComponentMap[parentId] = newComponentMap[
						parentId
					].filter(c => c !== removalComponent.id);
				}
				if (newComponentMap[removalComponent.id]) {
					delete newComponentMap[removalComponent.id];
				}

				// clean up the child map
				if (newChildrenComponentMap[removalComponent.id]) {
					delete newChildrenComponentMap[removalComponent.id];
				}
			}

			// dequeue the front of the list
			removalIds.splice(0, 1);
		}
	}

	// save state with updated data
	const newContainerNum =
		newState.allForms[formId].numContainers > 0
			? newState.allForms[formId].numContainers - 1
			: 0;

	const newForm = updateObject(newState.allForms[formId], {
		numContainers: newContainerNum,
		model: updateObject(newState.allForms[formId].model, {
			members: newMembers,
			componentMap: newComponentMap,
			childrenComponentMap: newChildrenComponentMap,
		}),
	});

	newState.allForms[formId] = newForm;
	newState = updateObject(newState, {
		lastUpdated: dateStamp,
	});

	//check validity
	containerListComponent.value = newContainerNum;

	const validityResult = checkValidityRules(containerListComponent);

	newState = checkValidity(newState, {
		validationResult: {
			data: {
				formId: formId,
				memberId: containerListKey,
				memberValid: validityResult.valid,
				touched:
					newState.allForms[formId].model.members[containerListKey]
						.validation.touched,
				validationMessage: validityResult.message,
				value: newContainerNum,
				visible:
					newState.allForms[formId].model.members[containerListKey]
						.visible,
			},
		},
	});

	return newState;
};

/**
 * revealValidation
 * mark all validation for a given form as "touched" and make validation messages visible
 * @param {*} state  current state
 * @param {*} action contains `formId` (int) which represents the tab index of the given application
 */
const revealValidation = (state, action) => {
	const formId = action.formId;
	let newState = { ...state };

	const newMembers = { ...newState.allForms[formId].model.members };

	Object.keys(newMembers).forEach(member => {
		//mark the component member as `touched`
		newMembers[member].validation.touched = true;

		//update the validation
		const targetComponentKey = member;
		const validityResult = checkValidityRules(
			newMembers[targetComponentKey]
		);
		let validationResult = {
			data: {
				formId: formId,
				memberId: targetComponentKey,
				memberValid: null,
				touched: null,
				validationMessage: null,
				value: null,
				visible: null,
			},
		};
		validationResult.data = {
			...validationResult.data,
			memberValid: validityResult.valid,
			touched: newMembers[targetComponentKey].validation.touched,
			validationMessage: validityResult.message,
			value: newMembers[member].value,
			visible: newMembers[targetComponentKey].visible,
		};

		newState = checkValidity(newState, {
			validationResult: validationResult,
		});
	});

	//update the state
	const newForm = updateObject(newState.allForms[formId], {
		model: updateObject(newState.allForms[formId].model, {
			members: newMembers,
		}),
	});

	newState.allForms[formId] = newForm;

	return newState;
};

const setAppDependencies = (state, action) => {
	return updateObject(state, {
		appDependencies: action.map,
	});
};

/**
 *
 * @param {JSON object for the incoming state} state
 * @param {string} action
 */
const setAppFormValidity = (state, action) => {
	const updatedState = { ...state };
	const updatedStateAllForms = { ...updatedState.allForms };
	updatedStateAllForms[action.setDetails.formIndex].valid =
		action.setDetails.validity;
	updatedState.allForms = updatedStateAllForms;

	//update the overall validity
	let newAllFormValidity = getOverallValidity(updatedState.allForms);
	updatedState.allFormValidity = newAllFormValidity;

	return updatedState;
};

const removeApp = (state, action) => {
	if (action === -1) {
		return state;
	}
	//remove UI component
	let removeId = state.activeIndex.toString();

	let appExists = false;
	let newForms = [];
	let newAllForms = [];

	for (let app in state.appsData) {
		if (app === removeId) {
			appExists = true;
		} else {
			newAllForms.push(state.allForms[app]);
			newForms.push(state.appsData[app]);
		}
	}

	//update the overall validity
	let newAllFormValidity = getOverallValidity(newAllForms);

	if (appExists) {
		return {
			...state,
			appsData: newForms,
			allForms: newAllForms,
			allFormValidity: newAllFormValidity,
		};
	} else {
		return state;
	}
};

/**
 *
 * @param {*} state
 * @param {*} action
 */
const updateApps = (state, action) => {
	return updateObject(state, {
		appsData: action.appsData.applications,
		rules: action.appsData.rules,
		rulesReverseLookup: action.appsData.rulesReverseLookup,
		appDependencies: action.appsData.dependencies,
	});
};

/**
 *
 * @param {*} state
 * @param {*} action
 */
const updateIndex = (state, action) => {
	return updateObject(state, {
		activeIndex: action.index,
	});
};

const updateLoading = (state, action) => {
	return updateObject(state, { loading: action.updatedValue });
};

const updateSubmitting = (state, action) => {
	return updateObject(state, {
		submitting: action.updatedValue,
		submissionAttempted: action.updatedValue || state.submissionAttempted,
	});
};

const updateNewSponsorESOID = (state, action) => {
	return updateObject(state, { newSponsorESOID: action.payload });
};

/**
 *
 * @param {*} state
 * @param {*} action
 */
const updateTabs = (state, action) => {
	let tabApps = [];
	for (let app in state.appsData) {
		const el = {
			id: state.appsData[app].id,
			name: state.appsData[app].Name,
		};
		tabApps.push(el);
	}

	return updateObject(state, { tabApps: tabApps });
};

/**
 *
 * @param {*} state
 * @param {*} action
 */
const updateTrackingNumber = (state, action) => {
	return updateObject(state, {
		trackingNumber: action.info,
	});
};

/**
 *
 * @param {*} state
 * @param {*} action
 */
const updateValidity = (state, action) => {
	let updatedFormValidity = { ...state.allForms };
	if (
		action.validationUpdate.data.MemberNames &&
		action.validationUpdate.data.MemberNames.length > 0
	) {
		//if ~~ detected, it is coming from validation override and all info is in MemberNames
		if (action.validationUpdate.data.MemberNames[0].includes('~~')) {
			for (let i in action.validationUpdate.data.MemberNames) {
				let tempValue =
					action.validationUpdate.data.MemberNames[i].split('~~');
				let targetMemberKey = tempValue[0];
				let formId = action.validationUpdate.formId;
				let errorMessage = tempValue[1];
				let updatedMemberValidation = {
					...updatedFormValidity[formId].model.members[
						targetMemberKey
					].validation,
				};
				updatedMemberValidation.valid = false;
				updatedMemberValidation.message = errorMessage;
				updatedFormValidity[formId].model.members[
					targetMemberKey
				].validation = updatedMemberValidation;
			}
		}
		//else, it is coming from the controller and info is in MemberNames and ErrorMessage
		else {
			let targetMemberKey = action.validationUpdate.data.MemberNames[0];
			let formId = action.validationUpdate.formId;
			let errorMessage = action.validationUpdate.data.ErrorMessage;
			let updatedMemberValidation = {
				...updatedFormValidity[formId].model.members[targetMemberKey]
					.validation,
			};
			updatedMemberValidation.valid = false;
			updatedMemberValidation.message = errorMessage;
			updatedFormValidity[formId].model.members[
				targetMemberKey
			].validation = updatedMemberValidation;
		}
	}

	return updateObject(state, {
		allFormValidity: false,
		allForms: updatedFormValidity,
	});
};

const updateRequestType = (state, action) => {
	if (
		action.requestType === RequestTypes.ANNUAL_REVIEW ||
		action.requestType === RequestTypes.SINGLE ||
		action.requestType === RequestTypes.MULTI_USER ||
		action.requestType === RequestTypes.SERVICE_ACCOUNT ||
		action.requestType === RequestTypes.MULTI_SERVICE_ACCOUNT
	) {
		return updateObject(state, {
			requestType: action.requestType,
		});
	} else {
		return state;
	}
};

/**
 *
 * @param {*} state
 * @param {*} action
 */
const updateValue = (state, action) => {
	const targetAppId = action.valueUpdate.appId;
	const targetComponentKey = action.valueUpdate.componentKey;
	const updatedValue = action.valueUpdate.newValue;
	const formId = getFormIdByAppId(state, targetAppId);

	let newState = { ...state };
	const newMembers = { ...newState.allForms[formId].model.members };
	newMembers[targetComponentKey].value = updatedValue;

	// check validation
	let validationResult = {
		data: {
			formId: formId,
			memberId: targetComponentKey,
			memberValid: null,
			validationMessage: null,
			value: null,
			visible: null,
		},
	};
	const validityResult = checkValidityRules(newMembers[targetComponentKey]);
	validationResult.data = {
		...validationResult.data,
		memberValid: validityResult.valid,
		validationMessage: validityResult.message,
		value: newMembers[targetComponentKey].value,
		visible: newMembers[targetComponentKey].visible,
	};
	newState = checkValidity(newState, {
		validationResult: validationResult,
	});

	const newForm = updateObject(newState.allForms[formId], {
		model: updateObject(newState.allForms[formId].model, {
			members: newMembers,
		}),
	});

	newState.allForms[formId] = newForm;

	return newState;
};

const updateContainerValue = (state, action) => {
	const targetAppId = action.containerInfo.appId;
	const targetComponentKey = action.containerInfo.componentKey;
	const formId = getFormIdByAppId(state, targetAppId);
	const targetComponentId =
		state.allForms[formId].model.members[targetComponentKey].id;
	const targetComponentPrefix = targetComponentKey.split('_')[0];
	let value = '';

	let childIds = state.allForms[formId].model.componentMap[targetComponentId];
	let valueStack = [];
	if (childIds && childIds.length > 0) {
		childIds.forEach(childId => {
			const child = getComponentByComponentId(
				childId,
				state.allForms[formId].model.members,
				targetComponentPrefix
			);

			if (child !== null && child.value !== null) {
				valueStack.push(child.value);
			}
		});
	}
	value = valueStack.join(', ');

	if (state.numContainer > 0) {
	}
	const updatedState = updateValue(state, {
		valueUpdate: {
			appId: targetAppId,
			componentKey: targetComponentKey,
			newValue: value,
		},
	});

	return updatedState;
};

const updateMember = (state, formId, newMember, memberName) => {
	const memberKey = memberName ? memberName : generateMemberKey(newMember);
	const newState = { ...state };
	const newAllForms = { ...newState.allForms };
	const newAllForm = newAllForms[formId];
	const newModel = { ...newAllForm.model };
	const newMembers = { ...newModel.members };
	newMembers[memberKey] = newMember;
	newAllForm.valid = getFormValidity(newMembers, true);

	newModel.members = newMembers;
	newAllForm.model = newModel;
	newState.allForms = newAllForms;
	newState.allFormValidity = getOverallValidity(newAllForms);
	return newState;
};

const updateMemberValue = (state, action) => {
	const memberInfo = action.payload;
	const formId = memberInfo.formId;
	const newMember = memberInfo.member;
	const memberName = memberInfo.memberName;
	const newState = updateMember(state, formId, newMember, memberName);

	return newState;
};

const updateMemberValidity = (state, action) => {
	const memberInfo = action.payload;
	const { formId, touched, validityData } = memberInfo;
	const newMember = memberInfo.member;
	const memberName = memberInfo.memberName;
	const validationMessage = validityData.message;
	const valid = validityData.valid;
	newMember.validation.valid = newMember.visible ? valid : true;
	newMember.validation.trueValidity = valid;
	newMember.validation.message = validationMessage;
	newMember.validation.touched = touched;

	const newState = updateMember(state, formId, newMember, memberName);
	return newState;
};

const updateMemberVisibility = (state, action) => {
	const memberInfo = action.payload;
	const { formId, visibility, member, memberName } = memberInfo;

	if (visibility) {
		member.validation.valid = member.validation.trueValidity;
	} else {
		member.validation.valid = true;
	}

	member.visible = visibility;

	const newState = updateMember(state, formId, member, memberName);
	return newState;
};

const resetStore = () => {
	return initialState;
};

const reducer = (state = initialState, action) => {
	switch (action.type) {
		case actionTypes.ADD_APP:
			return addApp(state, action);
		case actionTypes.ADD_NEW_CONTAINER:
			return addNewContainer(state, action);
		case actionTypes.APP_SUBMITTED:
			return appSubmitted(state, action);
		case actionTypes.CALL_API_POPULATE_START:
			return callApiPopulateStart(state, action);
		case actionTypes.CALL_API_POPULATE_FAIL:
			return callApiPopulateFail(state, action);
		case actionTypes.CALL_API_POPULATE_SUCCESS:
			return callApiPopulateSuccess(state, action);
		case actionTypes.CALL_API_VALIDATE:
			return callApiValidate(state, action);
		case actionTypes.CHECK_VALIDITY:
			return checkValidity(state, action);
		case actionTypes.CHECK_ALL_VALIDITY:
			return checkAllValidity(state, action);
		case actionTypes.REMOVE_APP:
			return removeApp(state, action);
		case actionTypes.REMOVE_CONTAINER:
			return removeContainer(state, action);
		case actionTypes.REVEAL_VALIDATION:
			return revealValidation(state, action);
		case actionTypes.RUN_RULES:
			return runRules(state, action);
		case actionTypes.SET_APPFORMVALIDITY:
			return setAppFormValidity(state, action);
		case actionTypes.SET_APP_DEPENDENCIES:
			return setAppDependencies(state, action);
		case actionTypes.UPDATE_APPS:
			return updateApps(state, action);
		case actionTypes.UPDATE_INDEX:
			return updateIndex(state, action);
		case actionTypes.UPDATE_LOADING:
			return updateLoading(state, action);
		case actionTypes.UPDATE_SUBMITTING:
			return updateSubmitting(state, action);
		case actionTypes.UPDATE_NEW_SPONSOR_ESOID:
			return updateNewSponsorESOID(state, action);
		case actionTypes.UPDATE_TABS:
			return updateTabs(state, action);
		case actionTypes.UPDATE_TRACKINGNUMBER:
			return updateTrackingNumber(state, action);
		case actionTypes.UPDATE_VALUE:
			return updateValue(state, action);
		case actionTypes.UPDATE_CONTAINER_VALUE:
			return updateContainerValue(state, action);
		case actionTypes.UPDATE_VALIDITY:
			return updateValidity(state, action);
		case actionTypes.UPDATE_REQUEST_TYPE:
			return updateRequestType(state, action);
		case actionTypes.UPDATE_MEMBER_VALUE:
			return updateMemberValue(state, action);
		case actionTypes.UPDATE_MEMBER_VALIDITY:
			return updateMemberValidity(state, action);
		case actionTypes.UPDATE_MEMBER_VISIBILITY:
			return updateMemberVisibility(state, action);
		case actionTypes.RESET_STORE:
			return resetStore();
		default:
			return state;
	}
};

export default reducer;
